import type { ReactNode, SelectHTMLAttributes, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

interface FieldWrapProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

function FieldWrap({ id, label, hint, error, required, children }: FieldWrapProps) {
  return (
    <div className="vn-field">
      <label className="vn-field__label" htmlFor={id}>
        {label}
        {required ? ' *' : ''}
      </label>
      {children}
      {hint && !error ? <span className="vn-field__hint">{hint}</span> : null}
      {error ? (
        <span className="vn-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
}

export function FormField({ id, label, hint, error, required, ...inputProps }: FormFieldProps) {
  return (
    <FieldWrap id={id} label={label} hint={hint} error={error} required={required}>
      <input
        id={id}
        className="vn-input"
        aria-invalid={error ? 'true' : undefined}
        required={required}
        {...inputProps}
      />
    </FieldWrap>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function SelectField({
  id,
  label,
  hint,
  error,
  required,
  options,
  placeholder,
  ...selectProps
}: SelectFieldProps) {
  return (
    <FieldWrap id={id} label={label} hint={hint} error={error} required={required}>
      <select id={id} className="vn-select" required={required} {...selectProps}>
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrap>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({ id, label, hint, error, required, ...textareaProps }: TextAreaFieldProps) {
  return (
    <FieldWrap id={id} label={label} hint={hint} error={error} required={required}>
      <textarea
        id={id}
        className="vn-textarea"
        aria-invalid={error ? 'true' : undefined}
        required={required}
        {...textareaProps}
      />
    </FieldWrap>
  );
}
